<template>
    <div class="f fv fa-c dw-10">
        <DashboardRow v-for="(panel, index) in panels"
            :key="panel.uid"
            :index="index"
            :dashboardType="dashboardType"
            :editMode="editMode"
            :availableWeight="availableWeight"
            :canAddPages="canAddPages"
            :hoveredPanelUid="hoveredPanelUid"
            :hoveredChildUid="hoveredChildUid"
            :draggedPanelUid="draggedPanelUid"
            :draggedChildUid="draggedChildUid"
            :draggedChildPanelUid="draggedChildPanelUid"
            :showDevFeatures="showDevFeatures"
            @onDragStart="onDragStart"
            @onDragEnter="onDragEnter"
            @onDrop="onDrop"
            @onChildDragStart="onChildDragStart"
            @onChildDragEnter="onChildDragEnter"
            @onChildDrop="onChildDrop"
            @onRemoveChildItem="onRemoveChildItem"
            @onAddChildItem="onAddChildItem"
            @onUpdateChildItem="onUpdateChildItem"
            @onCopyChildItem="onCopyChildItem"
            @onRemoveRow="onRemoveRow"
            @onRowUpdated="onRowUpdated"
            :dashboard="dashboard"
            :layoutMode="layoutMode"
            :panel="panel" />
        <AddSectionLine v-if="editMode"
            :rowCount="panels.length"
            :dashboardType="dashboardType"
            :availableWeight="availableWeight"
            :canAddPages="canAddPages"
            @onAddRow="onAddRow" />
    </div>
</template>
<script>
import {guuid, isNeArray, isNumber} from '@aretheyhappy/utils';
import DashboardRow from './DashboardRow.vue';
import AddSectionLine from './DashboardAddSectionLine.vue';

export default {
    name: 'mead-dashboard',
    props: {
        /**
         * Current dashboard type being displayed. Used to look up the dashboard
         * definition for computing available widget weight.
         */
        dashboardType: {type: String, default: ''},
        /**
         * Current set of dashboard rows being shown. Each row/panel is rendered
         * as a DashboardRow and reordered via drag and drop.
         */
        panels: {type: Array, default: () => []},
        /**
         * If we are currently in edit mode.
         */
        editMode: {type: Boolean, default: false},
        /**
         * If new pages can be added.
         */
        canAddPages: {type: Boolean, default: false},
        /**
         * If dev features can be shown.
         */
        showDevFeatures: {type: Boolean, default: false},
        /**
         * Entire Dashboard object.
         */
        dashboard: {type: Object, default: () => ({})},
        /**
         * How widgets are laid out inside each row. `flex` uses the flexboxgrid
         * row/col classes. `grid` uses a 12 column CSS grid where widgets can
         * also span 1 - 4 rows (via `options.row_span`), allowing a tall widget
         * to sit beside several stacked widgets.
         */
        layoutMode: {type: String, default: 'flex', validator: value => ['flex', 'grid'].includes(value)},
    },
    components: {DashboardRow, AddSectionLine},
    data: function () {
        return {
            uid: guuid(),
            // Uid of panel being dragged during drag and drop
            draggedPanelUid: '',
            // Uid of panel being hoverd in drag and drop
            hoveredPanelUid: '',
            // Uid of child item being dragged
            draggedChildUid: '',
            // Uid of parent uid for dragged child
            draggedChildPanelUid: '',
            // Uid of child item being hovered
            hoveredChildUid: '',
        };
    },
    methods: {
        /**
         * Called when a child panel drag start
         * @param panelUid - uid for the parent panel
         * @param uid - uid for the child panel
         */
        onChildDragStart: function (panelUid, uid) {
            this.draggedChildPanelUid = panelUid;
            this.draggedChildUid = uid;
        },
        /**
         * Called when a child panel drag enter happens
         * @param panelUid - uid for the parent panel
         * @param uid - uid for the child panel
         */
        onChildDragEnter: function (panelUid, uid) {
            // Ignore enter events from children that are not in the same parent panel
            if (panelUid !== this.draggedChildPanelUid) return;
            this.hoveredChildUid = uid;

        },
        /**
         * When a child drop event happens
         * @param panelUid - uid for the parent panel
         */
        onChildDrop: function (panelUid) {
            // 1. Ignore enter events from children that are not in the same parent panel
            // 2. If dragged item and hovered item are the same, dont do anything
            // 3. No Valid hover target
            if ((panelUid !== this.draggedChildPanelUid) ||
                (this.draggedChildUid === this.hoveredChildUid) ||
                (this.hoveredChildUid === '')) {
                this.clearDragDropValues();
                return;
            }

            // Find target panel object
            let targetPanel = false;
            for (const panel of this.panels) {
                if (panel.uid === this.draggedChildPanelUid) {
                    targetPanel = panel;
                    break;
                }
            }

            // Find target child
            let targetChild = false;
            for (const child of targetPanel.cols) {
                if (child.uid === this.draggedChildUid) {
                    targetChild = child;
                    break;
                }
            }

            if (targetPanel !== false && targetChild !== false) {
                // Create new sorted list of child panels
                const resortedChildren = [];
                for (let i = 0; i < targetPanel.cols.length; i++) {
                    if (targetPanel.cols[i].uid === this.draggedChildUid) {
                        continue;
                    }
                    if (targetPanel.cols[i].uid === this.hoveredChildUid) resortedChildren.push(targetChild)
                    resortedChildren.push(targetPanel.cols[i]);
                }

                // Replace cols
                targetPanel.cols = resortedChildren;
            }

            // Clear our drag and drop values
            this.clearDragDropValues();

            // Tell parent panel that we have updated the config
            this.$emit('onConfigUpdate', this.panels);
        },
        /**
         * Called when a dashboard row initiates drag start.
         * @param uid panel uid for the row
         */
        onDragStart: function (uid) {
            this.draggedPanelUid = uid;
        },
        /**
         * Called when a dashboard row is hovered over in drag mode.
         * @param uid panel uid for the row
         */
        onDragEnter: function (uid) {
            this.hoveredPanelUid = uid;
        },
        /**
         * When a panel drop event happens
         */
        onDrop: function () {
            // 1. If dragged item and hovered item are the same, dont do anything
            // 2. No valid hover target
            if ((this.draggedPanelUid === this.hoveredPanelUid) ||
                (this.hoveredPanelUid === '')) {
                // Clear drag and drop values
                this.clearDragDropValues();
                return;
            }

            // Find target panel object
            let targetPanel = false;
            for (const panel of this.panels) {
                if (panel.uid === this.draggedPanelUid) {
                    targetPanel = panel;
                    break;
                }
            }

            if (targetPanel === false) {
                // Clear drag and drop values
                this.clearDragDropValues();
                return;
            }

            // Create new sorted list of panels
            const resortedPanels = [];
            for (let i = 0; i < this.panels.length; i++) {
                if (this.panels[i].uid === this.draggedPanelUid) {
                    continue;
                }
                if (this.panels[i].uid === this.hoveredPanelUid) resortedPanels.push(targetPanel)
                resortedPanels.push(this.panels[i]);
            }

            // Clear drag and drop values
            this.clearDragDropValues();

            // Tell parent panel that we have updated the config
            this.$emit('onConfigUpdate', resortedPanels);
        },
        /**
         * Remove a child from a panel and notify consumer
         * @param panelUid
         * @param uid
         */
        onRemoveChildItem: function (panelUid, uid) {
            // Find target panel object
            let targetPanel = false;
            for (const panel of this.panels) {
                if (panel.uid === panelUid) {
                    targetPanel = panel;
                    break;
                }
            }

            if (targetPanel === false) {
                return;
            }

            // Create new  list of child panels
            const newChilren = [];
            for (let i = 0; i < targetPanel.cols.length; i++) {
                if (targetPanel.cols[i].uid === uid) {
                    continue;
                }
                newChilren.push(targetPanel.cols[i]);
            }

            // Replace children
            targetPanel.cols = newChilren;

            // Tell parent panel that we have updated the config
            this.$emit('onConfigUpdate', this.panels);
        },
        /**
         * Adds a child to the sent panel and updates consumer
         * @param panelUid
         * @param options
         */
        onAddChildItem: function (panelUid, options) {
            // Find panel and add child
            for (const panel of this.panels) {
                if (panel.uid === panelUid) {
                    panel.cols.push({
                        uid: guuid(),
                        widget: options.type,
                        options: options.options,
                    })
                    break;
                }
            }

            // Tell parent panel that we have updated the config
            this.$emit('onConfigUpdate', this.panels);
        },
        /**
         * When update event arrives from child rows
         * @param panelUid
         * @param childUid
         * @param options
         */
        onUpdateChildItem: function (panelUid, childUid, options) {
            // Find target panel object
            for (const panel of this.panels) {
                if (panel.uid === panelUid) {
                    // Find target child
                    for (const child of panel.cols) {
                        // Update child options
                        if (child.uid === childUid) {
                            child.options = options;
                            break;
                        }
                    }
                    break;
                }
            }

            // Tell parent panel that we have updated the config
            this.$emit('onConfigUpdate', this.panels);
        },
        /**
         * When duplicate event arrives from child rows
         * @param panelUid
         * @param childUid
         */
        onCopyChildItem: function (panelUid, childUid) {
            // Find target panel object
            for (const panel of this.panels) {
                if (panel.uid !== panelUid) continue;

                const childIndex = panel.cols.findIndex(el => el.uid === childUid);
                if (childIndex < 0) return;

                panel.cols.splice(childIndex + 1, 0, Object.assign({}, panel.cols[childIndex], {uid: guuid()}));

                break;
            }

            // Tell parent panel that we have updated the config
            this.$emit('onConfigUpdate', this.panels);
        },
        /**
         * Add a new section to the list of panels by type
         * @param rowType
         */
        onAddRow: function (rowType) {
            const panelUid = guuid();
            const panels = this.panels;
            panels.push({
                uid: panelUid,
                type: rowType,
                cols: []
            });
            // Tell parent panel that we have updated the config
            this.$emit('onConfigUpdate', panels);

            // Check if the selected row only has one child type and with no config and auto add that too
            const rowDefinition = MeadCommon.DashboardService.getRowDefinition(rowType);
            // Row only has one child
            if (rowDefinition.getAccessibleChildren().length === 1) {
                const child_type = rowDefinition.getAccessibleChildren()[0];
                const child_definition = MeadCommon.DashboardService.getWidgetDefinition(rowDefinition.getAccessibleChildren()[0]);
                // Child widget has no config step
                if (child_definition && !child_definition.hasEditConfiguration) {
                    this.onAddChildItem(panelUid, {
                        type: child_type,
                        options: child_definition.getDefaultProps(),
                    })
                    return;
                }
            }
        },
        /**
         * Remove a row from the dashboard
         * @param panelUid
         */
        onRemoveRow: function (panelUid) {
            const newPanels = [];
            // Find target panel object
            for (const panel of this.panels) {
                if (panel.uid === panelUid) {
                    continue;
                }
                newPanels.push(panel);
            }

            // Tell parent panel that we have updated the config
            this.$emit('onConfigUpdate', newPanels);
        },
        /**
         * Row configuration has updated from a row
         * @param panelUid
         * @param options
         */
        onRowUpdated: function (panelUid, options) {
            const updated_panels = [];
            // Find target panel object
            for (let i = 0; i < this.panels.length; i++) {
                if (this.panels[i].uid === panelUid) {
                    updated_panels.push(Object.assign({}, this.panels[i], options));
                }
                else {
                    updated_panels.push(this.panels[i]);
                }
            }

            // Tell parent panel that we have updated the config
            this.$emit('onConfigUpdate', updated_panels);
        },
        clearDragDropValues: function () {
            this.draggedPanelUid = '';
            this.hoveredPanelUid = '';
            this.draggedChildPanelUid = '';
            this.draggedChildUid = '';
            this.hoveredChildUid = '';
        },
    },
    computed: {
        widgetsWeight: function () {
            if (!isNeArray(this.panels)) return 0;

            let weight = 0;

            for (const panel of this.panels) {
                if (!isNeArray(panel.cols)) continue;

                for (const el of panel.cols) {
                    const widget = MeadCommon.DashboardService.getWidgetDefinition(el.widget);
                    if (widget === undefined) continue;
                    if (!isNumber(widget.getWeight())) continue;

                    weight += widget.getWeight();
                }
            }

            return weight;
        },
        maxWeight: function () {
            return MeadCommon.DashboardService.getDashboardDefinition(this.dashboardType).getMaxWeight();
        },
        availableWeight: function () {
            return this.maxWeight - this.widgetsWeight;
        },
    },
}
</script>